// src/components/ClientManagement.js
import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  doc,
  deleteDoc,
} from "firebase/firestore";
import {
  Grid,
  Paper,
  Typography,
  Box,
  List,
  ListItemText,
  CircularProgress,
  IconButton,
  ListItemButton,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SortIcon from "@mui/icons-material/Sort";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import AddClient from "./AddClient";
import { useNavigation } from "../contexts/NavigationContext";

function ClientManagement({ onClientSelect }) {
  const { selectClient } = useNavigation();
  
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'grid'
  
  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const clientsQuery = query(
      collection(db, "customers"),
      orderBy("name", "asc")
    );
    const unsubscribe = onSnapshot(
      clientsQuery,
      (querySnapshot) => {
        const clientsList = [];
        querySnapshot.forEach((doc) => {
          clientsList.push({ id: doc.id, ...doc.data() });
        });
        setClients(clientsList);
        setIsLoading(false);
      },
      (err) => {
        console.error("Error fetching clients:", err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Filter and sort clients
  const filteredAndSortedClients = useMemo(() => {
    let filtered = [...clients];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (client) =>
          client.name?.toLowerCase().includes(query) ||
          client.contactEmail?.toLowerCase().includes(query) ||
          client.contactPhone?.toLowerCase().includes(query) ||
          client.address?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return (a.name || "").localeCompare(b.name || "");
        case "email":
          return (a.contactEmail || "").localeCompare(b.contactEmail || "");
        case "newest":
          return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
        case "oldest":
          return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [clients, searchQuery, sortBy]);

  // Handle menu
  const handleMenuOpen = (event, client) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedClient(null);
  };

  const handleViewClient = () => {
    if (selectedClient) {
      if (onClientSelect) {
        onClientSelect(selectedClient.id);
      } else {
        selectClient(selectedClient.id);
      }
    }
    handleMenuClose();
  };

  const handleEditClient = () => {
    // Navigate to edit - you can implement this
    console.log("Edit client:", selectedClient);
    handleMenuClose();
  };

  // Handle delete
  const handleDeleteClick = (event, client) => {
    event?.stopPropagation();
    setClientToDelete(client);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setClientToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;

    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, "customers", clientToDelete.id));
      setSnackbar({
        open: true,
        message: `${clientToDelete.name} deleted successfully!`,
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    } catch (err) {
      console.error("Error deleting client:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete client",
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClientClick = (clientId) => {
    if (onClientSelect) {
      onClientSelect(clientId);
    } else {
      selectClient(clientId);
    }
  };

  // Render client card (grid view)
  const renderClientCard = (client) => (
    <Grid item xs={12} sm={6} md={4} key={client.id}>
      <Card
        sx={{
          height: "100%",
          cursor: "pointer",
          transition: "all 0.2s",
          border: "1px solid rgba(148,163,184,0.2)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 24px rgba(59,130,246,0.2)",
            borderColor: "primary.main",
          },
        }}
        onClick={() => handleClientClick(client.id)}
      >
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: "primary.main",
                fontSize: "1.25rem",
                fontWeight: 700,
              }}
            >
              {client.name?.[0]?.toUpperCase() || "C"}
            </Avatar>
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, client)}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {client.name || "Unnamed Client"}
          </Typography>

          <Stack spacing={1}>
            {client.contactEmail && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailIcon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {client.contactEmail}
                </Typography>
              </Box>
            )}
            {client.contactPhone && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {client.contactPhone}
                </Typography>
              </Box>
            )}
            {client.address && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocationOnIcon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {client.address}
                </Typography>
              </Box>
            )}
            {!client.contactEmail && !client.contactPhone && !client.address && (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No contact details
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );

  // Render client list item (list view)
  const renderClientListItem = (client) => {
    const secondaryLines = [];

    if (client.contactEmail) {
      secondaryLines.push(`📧 ${client.contactEmail}`);
    }
    if (client.contactPhone) {
      secondaryLines.push(`📞 ${client.contactPhone}`);
    }
    if (client.address) {
      secondaryLines.push(`📍 ${client.address}`);
    }

    return (
      <ListItemButton
        key={client.id}
        divider
        onClick={() => handleClientClick(client.id)}
        sx={{
          py: 1.5,
          px: 2,
          borderRadius: 2,
          mb: 0.5,
          transition: "all 0.2s",
          "&:hover": {
            bgcolor: "rgba(56,189,248,0.08)",
            transform: "translateX(4px)",
          },
        }}
      >
        <Avatar
          sx={{
            mr: 2,
            bgcolor: "primary.main",
            width: 40,
            height: 40,
            fontWeight: 700,
          }}
        >
          {client.name?.[0]?.toUpperCase() || "C"}
        </Avatar>
        <ListItemText
          primary={
            <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
              {client.name || "Unnamed Client"}
            </Typography>
          }
          secondary={
            secondaryLines.length > 0
              ? secondaryLines.join(" • ")
              : "No contact details saved."
          }
          secondaryTypographyProps={{
            variant: "body2",
            color: "text.secondary",
          }}
        />
        <IconButton
          edge="end"
          onClick={(e) => handleMenuOpen(e, client)}
        >
          <MoreVertIcon />
        </IconButton>
      </ListItemButton>
    );
  };

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
            <BusinessIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Client Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your customers and their details
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, rgba(37,99,235,0.15), rgba(15,23,42,0.95))",
            border: "1px solid rgba(59,130,246,0.35)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  <BusinessIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {clients.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Clients
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(15,23,42,0.9))",
            border: "1px solid rgba(34,197,94,0.35)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "success.main" }}>
                  <PersonAddIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {filteredAndSortedClients.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? "Filtered" : "Active"}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, rgba(56,189,248,0.18), rgba(15,23,42,0.9))",
            border: "1px solid rgba(59,130,246,0.3)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "info.main" }}>
                  <EmailIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {clients.filter(c => c.contactEmail).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    With Email
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(15,23,42,0.9))",
            border: "1px solid rgba(139,92,246,0.35)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "secondary.main" }}>
                  <PhoneIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {clients.filter(c => c.contactPhone).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    With Phone
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Left: Add Client */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              border: "1px solid rgba(148,163,184,0.2)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Avatar sx={{ bgcolor: "success.main" }}>
                <PersonAddIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Add New Client
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Create a new customer record with contact details and address.
            </Typography>
            <Divider sx={{ my: 1 }} />
            <AddClient />
          </Paper>
        </Grid>

        {/* Right: List of Clients */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              border: "1px solid rgba(148,163,184,0.2)",
            }}
          >
            {/* Controls */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  All Clients ({clients.length})
                </Typography>
                
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="list">
                    <Tooltip title="List View">
                      <ViewListIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="grid">
                    <Tooltip title="Grid View">
                      <ViewModuleIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search clients by name, email, phone, or address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  select
                  size="small"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ minWidth: 150 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SortIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem value="name">Name A-Z</MenuItem>
                  <MenuItem value="email">Email A-Z</MenuItem>
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                </TextField>
              </Stack>

              {searchQuery && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Showing {filteredAndSortedClients.length} of {clients.length} clients
                </Typography>
              )}
            </Box>

            {/* Content */}
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 6,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : filteredAndSortedClients.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <BusinessIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchQuery ? "No Matching Clients" : "No Clients Yet"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {searchQuery
                    ? "Try adjusting your search"
                    : "Add your first client using the form"}
                </Typography>
                {searchQuery && (
                  <Button
                    variant="outlined"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear Search
                  </Button>
                )}
              </Box>
            ) : viewMode === "grid" ? (
              <Grid container spacing={2} sx={{ overflow: "auto" }}>
                {filteredAndSortedClients.map(renderClientCard)}
              </Grid>
            ) : (
              <List sx={{ overflow: "auto", flexGrow: 1 }}>
                {filteredAndSortedClients.map(renderClientListItem)}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Client Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewClient}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditClient}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Client</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDeleteClick(null, selectedClient)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Client?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{clientToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ClientManagement;